import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { signOut } from 'firebase/auth';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Toolbar,
  AppBar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

export default function AdminDashboard() {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [poolFilter, setPoolFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const studentsCollection = collection(db, 'students');
        const studentsSnapshot = await getDocs(studentsCollection);
        const studentsList = studentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStudents(studentsList);
        setFilteredStudents(studentsList);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching students:', error);
        setError('Failed to fetch student data');
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  useEffect(() => {
    // Apply filters whenever search term or pool filter changes
    let result = students;
    
    // Apply pool filter
    if (poolFilter !== 'all') {
      result = result.filter(student => student.pool === poolFilter);
    }
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(student => 
        (student.name && student.name.toLowerCase().includes(term)) ||
        (student.email && student.email.toLowerCase().includes(term)) ||
        (student.schoolName && student.schoolName.toLowerCase().includes(term)) ||
        (student.mobileNumber && student.mobileNumber.includes(term)) ||
        (student.parentName && student.parentName.toLowerCase().includes(term)) ||
        (student.schoolCity && student.schoolCity.toLowerCase().includes(term))
      );
    }
    
    setFilteredStudents(result);
  }, [searchTerm, poolFilter, students]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin-login');
    } catch (error) {
      console.error('Failed to log out', error);
      setError('Failed to log out. Please try again.');
    }
  };

  const exportToCSV = () => {
    // Create CSV content with proper formatting
    const headers = [
      'Name', 
      'Email', 
      'Mobile Number', 
      'Age', 
      'Pool', 
      'Class', 
      'School Name', 
      'School City', 
      'Parent Name', 
      'Parent Mobile'
    ];
    
    const csvRows = [];
    
    // Add headers
    csvRows.push(headers.join(','));
    
    // Add data rows
    filteredStudents.forEach(student => {
      const row = [
        `"${student.name || ''}"`,
        `"${student.email || ''}"`,
        `"${student.mobileNumber || ''}"`,
        `"${student.age || ''}"`,
        `"${student.pool || ''}"`,
        `"${student.class || ''}"`,
        `"${student.schoolName || ''}"`,
        `"${student.schoolCity || ''}"`,
        `"${student.parentName || ''}"`,
        `"${student.parentMobile || ''}"`
      ];
      csvRows.push(row.join(','));
    });
    
    // Create CSV content with proper line breaks
    const csvContent = csvRows.join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `students_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <AppBar position="static" sx={{ mb: 4 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="xl">
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">Student Records</Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<FileDownloadIcon />}
              onClick={exportToCSV}
            >
              Export to CSV
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', mb: 3, gap: 2 }}>
            <TextField
              label="Search by name, email, school, mobile, parent name, or city"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1 }}
              InputProps={{
                endAdornment: <SearchIcon />
              }}
            />
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Pool</InputLabel>
              <Select
                value={poolFilter}
                label="Pool"
                onChange={(e) => setPoolFilter(e.target.value)}
              >
                <MenuItem value="all">All Pools</MenuItem>
                <MenuItem value="A">Pool A</MenuItem>
                <MenuItem value="B">Pool B</MenuItem>
                <MenuItem value="C">Pool C</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Showing {filteredStudents.length} of {students.length} students
          </Typography>
          
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Mobile</strong></TableCell>
                  <TableCell><strong>Age</strong></TableCell>
                  <TableCell><strong>Pool</strong></TableCell>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell><strong>School Name</strong></TableCell>
                  <TableCell><strong>School City</strong></TableCell>
                  <TableCell><strong>Parent Name</strong></TableCell>
                  <TableCell><strong>Parent Mobile</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.length > 0 ? (
                  filteredStudents.map((student) => (
                    <TableRow key={student.id} hover>
                      <TableCell>{student.name || 'N/A'}</TableCell>
                      <TableCell>{student.email || 'N/A'}</TableCell>
                      <TableCell>{student.mobileNumber || 'N/A'}</TableCell>
                      <TableCell>{student.age || 'N/A'}</TableCell>
                      <TableCell>{student.pool ? `Pool ${student.pool}` : 'N/A'}</TableCell>
                      <TableCell>{student.class || 'N/A'}</TableCell>
                      <TableCell>{student.schoolName || 'N/A'}</TableCell>
                      <TableCell>{student.schoolCity || 'N/A'}</TableCell>
                      <TableCell>{student.parentName || 'N/A'}</TableCell>
                      <TableCell>{student.parentMobile || 'N/A'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No students found matching the criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </>
  );
} 